/**
 * Web MIDI access, raw — no library, no sysex.
 *
 * ⚠️ NEVER PROMPT AT LOAD — request from a user action (entering Learn, enabling a device): Chrome 124+ prompts for MIDI itself, and a prompt nobody asked for at page load reads as the app misbehaving. The one exception is a permission already granted, which resolves without a prompt (`resumeMidi`, `../index.js`). Safari (iOS included) has no Web MIDI at all; Firefox needs a site-permission add-on.
 */
import { IS_LOCAL } from '~/lib/constants'

export const midiSupported = () => typeof navigator !== 'undefined' && typeof navigator.requestMIDIAccess === 'function'

let pending = null

// Dev: the virtual controllers' ports join the browser's (`lib/virtualMidi`).
const request = () =>
	IS_LOCAL
		? import('~/lib/virtualMidi').then(v => v.requestAccess({ sysex: false }))
		: navigator.requestMIDIAccess({ sysex: false })

/** The one `MIDIAccess`, requested once. A refusal is not cached: the next user action may ask again. */
export const requestMidi = () => {
	if (!midiSupported())
		return Promise.reject(Object.assign(new Error('Web MIDI unsupported'), { name: 'NotSupportedError' }))
	pending ??= request().catch(e => {
		pending = null
		throw e
	})
	return pending
}

const connected = ports => [...(ports?.values() ?? [])].filter(p => p.state !== 'disconnected')

export const connectedInputs = access => connected(access?.inputs)

export const connectedOutputs = access => connected(access?.outputs)
